import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import AgentFace from '../../../common/components/agent-face/agent-face';
import Picker from '../../../common/components/picker/picker';
import Toggle from '../../../common/components/toggle/toggle';
import { IconTrash } from '../../../common/icons';
import { formatAgo, formatDateTime } from '../../../core/utils/date';
import { useAuth } from '../../../global/contexts/auth/auth';
import { useRepositories } from '../../../global/contexts/lookups/lookups';
import KindBadge from '../../repositories/layouts/kind-badge';
import { TOKENS_PAGE_SIZE, useRevokeToken, useTokensList } from '../contexts/tokens';
import { SORTS, isDormant, isExpired, isLive, isRevoked, sortTokens } from '../utils/filters';
import { TOKEN_KIND_OPTIONS } from '../utils/form';

import '../styles/overview.css';

// The credentials the agents act with.
//
// WHAT THIS SCREEN NEVER SHOWS is the token. The backend keeps an HMAC and the
// prefix; the plaintext exists once, in the answer to the POST that created it,
// and is never stored anywhere. So the list is a roster of credentials, not a
// keyring: who owns each one, what it can do, when it last acted, and the one
// button that matters — revoke.
//
// THERE IS NO EDIT, and that is the right call, not a gap: changing what a
// credential can do after handing it out means the copy somebody already has
// quietly changes meaning. Revoke and issue another.

const KIND_LABEL = Object.fromEntries(
    TOKEN_KIND_OPTIONS.map(option => [option.value, option.label]),
);

// The `status` enum every table in this backend shares. A token is revoked by
// going to 0, and the row stays: the tasks and events it signed keep pointing
// at something that exists.
const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Revocado' };

// El buscador del filtro de repositorio.
//
// Filtra EN MEMORIA el catálogo que `useRepositories()` ya trajo, porque el
// listado del backend no acepta `?q=`. Devuelve una promesa para encajar con la
// firma que el Picker espera —`(q, signal) => Promise<item[]>`— sin que el
// componente tenga que saber que acá no hay red de por medio.
function findRepository(repositories) {
    return (q) => {
        const all = repositories ?? [];
        if (!q) return Promise.resolve(all.slice(0, 20));

        const needle = q.toLowerCase();
        return Promise.resolve(
            all
                .filter(repository => (repository.name ?? '').toLowerCase().includes(needle))
                .slice(0, 20),
        );
    };
}

export default function TokensOverview() {
    const [params, setParams] = useSearchParams();
    const page = Math.max(Number(params.get('page') || 1), 1);
    const kind = params.get('kind') || '';
    const repositoryId = params.get('repository') || '';
    const offset = (page - 1) * TOKENS_PAGE_SIZE;

    const { data, isLoading, isFetching, error, dataUpdatedAt } = useTokensList({
        limit: TOKENS_PAGE_SIZE, offset, kind, repositoryId,
    });
    const { data: repositories } = useRepositories();
    const { can } = useAuth();
    const canOpenRepositories = can('repositories:write');

    // El catálogo indexado por id: lo consultan la fila (nombre y tipo) y nada
    // más, así que una sola vuelta en vez de un `find` por fila.
    const repositoryById = useMemo(
        () => new Map((repositories ?? []).map(item => [item.id, item])),
        [repositories],
    );

    // Su propio `useMemo` porque abajo es dependencia de otros dos: sin esto,
    // `data?.items ?? []` es un arreglo nuevo en cada render y los recalcula
    // siempre.
    const loaded = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? 0;
    const lastPage = Math.max(Math.ceil(total / TOKENS_PAGE_SIZE), 1);
    // The clock an expiry is judged against: when the list was fetched, not
    // when React last painted. Reading the wall clock during render is impure
    // and would flip a token from live to expired on an unrelated repaint.
    const countedAt = dataUpdatedAt || 0;

    // OCULTAR LOS INACTIVOS VIENE ENCENDIDO, y el parámetro dice lo contrario
    // para que la URL por defecto sea la corta: `?dormidos=1` los muestra.
    // Una credencial muerta no es una decisión pendiente; lo que no puede pasar
    // es esconderlas en silencio, y por eso la lista dice cuántas dejó afuera.
    const showDormant = params.get('dormidos') === '1';
    const sort = params.get('sort') || '';

    const hidden = useMemo(
        () => (showDormant ? 0 : loaded.filter(token => isDormant(token, countedAt)).length),
        [loaded, showDormant, countedAt],
    );

    // EL FILTRO Y EL ORDEN CORREN ACÁ. `GET /admin/tokens` toma `user_id`,
    // `kind` y `repository_id` —los tres del lado del servidor— y ni `status` ni
    // orden. Así que estos dos actúan sobre la página cargada, y la pantalla lo
    // dice cuando hay más.
    const items = useMemo(() => {
        const rows = showDormant ? loaded : loaded.filter(token => isLive(token, countedAt));
        return sortTokens(rows, sort);
    }, [loaded, showDormant, sort, countedAt]);

    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        target.delete('page');
        setParams(target);
    };

    const setPage = (value) => {
        const target = new URLSearchParams(params);
        target.set('page', String(value));
        setParams(target);
    };

    if (error) {
        return <p className="tokens__error">{error.message || 'No se pudieron cargar los tokens'}</p>;
    }

    return (
        <section className="tokens">
            <header className="tokens__header">
                <div className="tokens__heading">
                    <span className="tokens__hint">Credenciales</span>
                    <h2 className="tokens__title">Tokens y agentes</h2>
                </div>

                <Link to="/admin/tokens/new" className="tokens__new">+ Emitir token</Link>
            </header>

            <div className="tokens__filters">
                <label className="tokens__field">
                    <span className="tokens__field-label">Tipo</span>
                    <select
                        className="tokens__filter"
                        value={kind}
                        onChange={e => setFilter('kind', e.target.value)}
                    >
                        <option value="">Todos los tipos</option>
                        {TOKEN_KIND_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                {/* BUSCA SOBRE EL CATÁLOGO YA CARGADO, y el pie del panel lo
                    dice. `GET /admin/repositories` no acepta `?q=` —filtra por
                    project_id, kind y clone_protocol—, así que no hay búsqueda
                    de servidor que ofrecer: lo que hay es un cuadro que filtra
                    las cien filas que el catálogo trajo. Es mucho más que el
                    `<select>` que había, y no es «buscar entre todos». */}
                <label className="tokens__field">
                    <span className="tokens__field-label">Repositorio</span>
                    <Picker
                        value={repositoryId}
                        onChange={(id) => setFilter('repository', id)}
                        fetchOptions={findRepository(repositories)}
                        cacheKey="tokens:repositories"
                        labelOf={repository => repository.name || `#${repository.id}`}
                        subtitleOf={repository => repository.kind}
                        placeholder="Todos los repositorios"
                        emptyLabel="Todos los repositorios"
                        hint="Busca sobre los repositorios ya cargados, no contra el servidor."
                    />
                </label>

                <label className="tokens__field">
                    <span className="tokens__field-label">Orden</span>
                    <select
                        className="tokens__filter"
                        value={sort}
                        onChange={e => setFilter('sort', e.target.value)}
                    >
                        {SORTS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                <div className="tokens__switch">
                    <Toggle
                        checked={!showDormant}
                        onChange={(on) => setFilter('dormidos', on ? '' : '1')}
                        label="Ocultar inactivos"
                        hint="Revocados y vencidos"
                    />
                </div>
            </div>

            {hidden > 0 && (
                <p className="tokens__scope-note">
                    {hidden} credencial{hidden === 1 ? '' : 'es'} oculta{hidden === 1 ? '' : 's'}
                    {' '}por estar revocada{hidden === 1 ? '' : 's'} o vencida{hidden === 1 ? '' : 's'}.
                </p>
            )}

            {isLoading && <p className="tokens__empty">Cargando…</p>}

            {!isLoading && !items.length && (
                <p className="tokens__empty">
                    No hay tokens con este filtro. Sin token no hay agente: el sistema no
                    conoce procesos, conoce credenciales.
                </p>
            )}

            {items.length > 0 && (
                <ul className={`tokens__list${isFetching ? ' tokens__list--busy' : ''}`}>
                    {/* `ownerName` comes off the row: the list endpoint embeds
                        `user`, so nothing here has to hold a catalog of people
                        to print a name — and the screen works for somebody
                        without `users:read`. */}
                    {items.map(token => (
                        <TokenRow
                            key={token.id}
                            token={token}
                            ownerName={token.user?.name}
                            repositoryName={repositoryById.get(token.repository_id)?.name}
                            repositoryKind={repositoryById.get(token.repository_id)?.kind}
                            repositoryTo={canOpenRepositories && token.repository_id
                                ? `/admin/repositories/${token.repository_id}`
                                : null}
                            countedAt={countedAt}
                        />
                    ))}
                </ul>
            )}

            <footer className="tokens__foot">
                <p className="tokens__note">
                    El token en claro se ve <strong>una sola vez</strong>, cuando se emite. Acá
                    solo queda el prefijo: el servidor guarda un HMAC, no el token. Un token no
                    se edita — se revoca y se emite otro, porque cambiarle los permisos a una
                    credencial que ya está en manos de alguien la cambia sin avisarle.
                </p>

                {total > 0 && (
                    <div className="tokens__pager">
                        <button
                            type="button"
                            className="tokens__page"
                            onClick={() => setPage(page - 1)}
                            disabled={page <= 1}
                        >
                            Anterior
                        </button>
                        <span className="tokens__count">
                            {offset + 1}–{Math.min(offset + items.length, total)} de {total}
                        </span>
                        <button
                            type="button"
                            className="tokens__page"
                            onClick={() => setPage(page + 1)}
                            disabled={page >= lastPage}
                        >
                            Siguiente
                        </button>
                    </div>
                )}
            </footer>
        </section>
    );
}

function TokenRow({ token, ownerName, repositoryName, repositoryKind, repositoryTo, countedAt }) {
    const [confirming, setConfirming] = useState(false);
    const revoke = useRevokeToken();

    const revoked = isRevoked(token);
    const expired = isExpired(token, countedAt);
    const live = isLive(token, countedAt);
    // LA CARA ES EL ESTADO. Verde y quieta si la credencial autentica hoy;
    // apagada si dejó de hacerlo, por revocación o por vencimiento. Es el mismo
    // componente que usan el pulso y el tablero, así que una credencial muerta
    // se ve muerta en todas partes.
    //
    // Y NO ES 'working': ese late porque algo está pasando. Acá no pasa nada —
    // el token está disponible, que es otra afirmación.
    const face = live ? 'live' : 'idle';

    const onRevoke = async () => {
        try {
            await revoke.mutateAsync(token.id);
            toast.success(`Token «${token.name}» revocado`);
            setConfirming(false);
        } catch (err) {
            toast.error(err.message || 'No se pudo revocar el token');
        }
    };

    // «Vivo» NO es `status` 2: un token vencido sigue en 2 y dejó de servir.
    // Pintarlo de verde es la media hora que alguien pierde depurando del lado
    // del agente. La fila no se tiñe — el color va en la cara, que es lo que se
    // escanea; una lista con cuarenta filas de fondo verde no dice nada.
    return (
        <li className={`token${revoked ? ' token--revoked' : ''}`}>
            <AgentFace state={face} kind={token.kind} size={32} title={token.name} />

            <span className="token__identity">
                <span className="token__name">{token.name}</span>
                <code className="token__prefix">{token.token_prefix}…</code>
            </span>

            <span className="token__kind">{KIND_LABEL[token.kind] ?? token.kind}</span>

            <span className="token__owner">{ownerName ?? `Usuario #${token.user_id}`}</span>

            <span className="token__where">
                {token.repository_id
                    ? (
                        <>
                            {/* Forma y tono, igual que en el resto del panel. */}
                            {repositoryKind && <KindBadge kind={repositoryKind} label={false} />}
                            {repositoryTo
                                ? <Link to={repositoryTo}>{repositoryName ?? `Repositorio #${token.repository_id}`}</Link>
                                : (repositoryName ?? `Repositorio #${token.repository_id}`)}
                        </>
                    )
                    : (
                        /* Sin repositorio NO es «sin alcance»: es un token que
                           no angosta por ese eje, y alcanza lo que alcance su
                           dueño. La misma regla que los ejes de una asignación
                           de equipo y el alcance de un plan. */
                        <span className="token__none" title="No angosta por repositorio: alcanza lo que alcance su dueño">
                            sin restricción de repositorio
                        </span>
                    )}
            </span>

            <span className="token__when" title={token.last_used_at ? formatDateTime(token.last_used_at) : undefined}>
                {token.last_used_at ? formatAgo(token.last_used_at, countedAt) : 'nunca usado'}
            </span>

            <span className={`token__status token__status--${token.status}`}>
                {STATUS_LABEL[token.status] ?? token.status}
                {/* Expiry is not status: an expired token is still `status` 2 and
                    stops authenticating anyway. Two different ways of being
                    dead, and the row says which. */}
                {!revoked && expired && <span className="token__expired">vencido</span>}
            </span>

            <span className="token__actions">
                {revoked
                    ? <span className="token__none">—</span>
                    : confirming
                        ? (
                            <>
                                <button
                                    type="button"
                                    className="token__revoke token__revoke--yes"
                                    onClick={onRevoke}
                                    disabled={revoke.isPending}
                                >
                                    {revoke.isPending ? 'Revocando…' : 'Sí, revocar'}
                                </button>
                                <button
                                    type="button"
                                    className="token__revoke"
                                    onClick={() => setConfirming(false)}
                                >
                                    No
                                </button>
                            </>
                        )
                        : (
                            <button
                                type="button"
                                className="token__revoke"
                                onClick={() => setConfirming(true)}
                                title="Deja de autenticar de inmediato. La fila se conserva."
                            >
                                <IconTrash />
                                Revocar
                            </button>
                        )}
            </span>
        </li>
    );
}
